/**
 * UniversalExportModal Component
 *
 * Generic export modal that works for any entity type.
 * Driven by configuration object provided via props.
 *
 * FEATURES:
 * - Config-driven filter and column sections
 * - Conditional section visibility based on row selection
 * - React.memo optimization
 * - Integrates with existing ExportModal and ExportSection components
 * - Full type safety with NO 'as any' casts
 */

import React, { useCallback, useMemo } from 'react';

import { ExportModal } from '@/sections/export/components/ExportModal';
import type { ExportOption as SectionExportOption } from '@/sections/export/components/ExportSection';
import { ExportSection } from '@/sections/export/components/ExportSection';
import type { DropdownOption } from '@/types/dropdown.types';

import { useUniversalExport } from './hooks/useUniversalExport';
import type {
  ExportableEntity,
  ExportFormat,
  ExportOption,
  UniversalExportModalProps,
} from './types';

/**
 * Helper: Convert ExportFormat to DropdownOption
 */
function formatToDropdownOption(format: ExportFormat): DropdownOption {
  return {
    label: format.label,
    value: format.value,
  };
}

/**
 * Helper: Convert DropdownOption to ExportFormat
 */
function dropdownOptionToFormat(option: DropdownOption): ExportFormat {
  return {
    label: option.label,
    value: String(option.value || option.label),
  };
}

/**
 * Helper: Convert ExportOption to SectionExportOption
 */
function toSectionExportOption(option: ExportOption): SectionExportOption {
  return {
    label: option.label,
    value: option.value,
    severity: option.severity,
    variant: option.variant,
    icon: option.icon,
    count: option.count,
  };
}

/**
 * Helper: Convert SectionExportOption to ExportOption
 */
function fromSectionExportOption(option: SectionExportOption): ExportOption {
  return {
    label: option.label,
    value: option.value,
    severity: option.severity,
    variant: option.variant,
    icon: option.icon,
    count: typeof option.count === 'number' ? option.count : undefined,
  };
}

/**
 * UniversalExportModal Component
 *
 * @param props - Modal props including config and selection state
 *
 * USAGE:
 * ```typescript
 * <UniversalExportModal
 *   open={showModal}
 *   onClose={handleClose}
 *   config={assetExportConfig}
 *   selectedRows={selectedAssetKeys}
 *   onClearSelection={clearSelection}
 * />
 * ```
 */
function UniversalExportModalComponent<TEntity extends ExportableEntity>({
  open,
  onClose,
  config,
  selectedRows,
  onClearSelection,
}: UniversalExportModalProps<TEntity>) {
  // Use universal export hook with entity config
  const exportHook = useUniversalExport(config);

  // Convert selectedRows to string array for internal use
  const selectedRowStrings = useMemo(
    () => selectedRows as readonly string[],
    [selectedRows]
  );

  // Determine which filter sections to show
  const visibleFilterSections = useMemo(
    () =>
      config.filterSections.filter(section => {
        // Hide sections that shouldn't show when rows are selected
        if (selectedRowStrings.length > 0 && !section.showWhenSelected) {
          return false;
        }
        return true;
      }),
    [config.filterSections, selectedRowStrings.length]
  );

  // Handle export execution with immediate modal closure for async exports
  const handleExport = useCallback((): void => {
    // Start export asynchronously (don't await, let it run in background)
    // The useAsyncExports hook handles:
    // - Validation errors (will show toast)
    // - Toast notifications ("Exporting...", "Download" button on success)
    // - Job polling and status updates
    // - Error handling for async failures
    exportHook.executeExport(selectedRowStrings).catch(error => {
      // Log validation errors for debugging
      // (async errors are already handled by useAsyncExports toast)
      console.error('[UniversalExportModal] Export error:', error);
    });

    // Close modal IMMEDIATELY after starting export (don't wait for completion)
    // This provides the async UX: modal closes → toast shows "Exporting..." → polling happens in background
    onClose();
    onClearSelection?.();
  }, [exportHook, selectedRowStrings, onClose, onClearSelection]);

  // Convert format for ExportModal (memoized)
  const exportFormatDropdown = useMemo(
    () => formatToDropdownOption(exportHook.state.format),
    [exportHook.state.format]
  );

  // Handle format change (wrap with callback)
  const handleFormatChange = useCallback(
    (option: DropdownOption): void => {
      exportHook.updateFormat(dropdownOptionToFormat(option));
    },
    [exportHook]
  );

  // Map column configs to SectionExportOptions (memoized)
  const columnOptions = useMemo(
    (): SectionExportOption[] =>
      config.columns.map(col => ({
        label: col.label,
        value: col.id,
      })),
    [config.columns]
  );

  // Create memoized filter update handler to prevent inline function creation
  const createFilterUpdateHandler = useCallback(
    (sectionId: string) =>
      (options: SectionExportOption[]): void => {
        exportHook.updateFilter(
          sectionId,
          options.map(fromSectionExportOption)
        );
      },
    [exportHook]
  );

  // Create memoized column update handler
  const handleColumnsUpdate = useCallback(
    (options: SectionExportOption[]): void => {
      exportHook.updateColumns(options.map(fromSectionExportOption));
    },
    [exportHook]
  );

  return (
    <ExportModal
      open={open}
      onClose={onClose}
      type={config.entityType}
      selected={
        selectedRowStrings.length > 0 && onClearSelection
          ? {
              selectedRows: Array.from(selectedRowStrings),
              onClearSelectedRows: onClearSelection,
            }
          : undefined
      }
      disabled={exportHook.isExportDisabled(selectedRowStrings)}
      loading={exportHook.isLoading}
      exportFormat={exportFormatDropdown}
      onChangeExportFormat={handleFormatChange}
      onExport={handleExport}
    >
      {/* Render filter sections */}
      {visibleFilterSections.map(section => (
        <ExportSection
          key={section.id}
          label={section.label}
          options={section.options.map(toSectionExportOption)}
          styleType={section.styleType || 'table'}
          selected={
            exportHook.state.filters[section.id]?.map(toSectionExportOption) ||
            []
          }
          setSelected={createFilterUpdateHandler(section.id)}
        />
      ))}

      {/* Always render columns section */}
      <ExportSection
        label="Columns"
        styleType="columns"
        options={columnOptions}
        selected={exportHook.state.columns.map(toSectionExportOption)}
        setSelected={handleColumnsUpdate}
      />
    </ExportModal>
  );
}

/**
 * Memoized version for performance optimization
 *
 * Custom comparison function prevents unnecessary re-renders by checking:
 * - open state changes (modal visibility)
 * - config reference changes (export configuration)
 * - selectedRows array length and content (row selection state)
 *
 * Returns true if props are equal (skip re-render), false if different (re-render)
 */
const MemoizedUniversalExportModal = React.memo(
  UniversalExportModalComponent,
  <TEntity extends ExportableEntity>(
    prevProps: UniversalExportModalProps<TEntity>,
    nextProps: UniversalExportModalProps<TEntity>
  ): boolean => {
    // Check open state - critical for modal visibility
    if (prevProps.open !== nextProps.open) {
      return false;
    }

    // Check config reference - if config changes, we need to re-render
    if (prevProps.config !== nextProps.config) {
      return false;
    }

    // Check selectedRows length - quick first pass
    if (prevProps.selectedRows.length !== nextProps.selectedRows.length) {
      return false;
    }

    // Check selectedRows content - verify each item is the same
    // Uses strict equality check for primitive values or reference equality for objects
    const rowsEqual = prevProps.selectedRows.every(
      (row, index) => row === nextProps.selectedRows[index]
    );
    if (!rowsEqual) {
      return false;
    }

    // Check onClose reference (typically stable but worth checking)
    if (prevProps.onClose !== nextProps.onClose) {
      return false;
    }

    // Check onClearSelection reference (optional prop)
    if (prevProps.onClearSelection !== nextProps.onClearSelection) {
      return false;
    }

    // All props are equal - skip re-render for performance
    return true;
  }
);

// Set display name for React DevTools
MemoizedUniversalExportModal.displayName = 'UniversalExportModal';

// Export with proper typing - preserve generic type support
export const UniversalExportModal =
  MemoizedUniversalExportModal as typeof UniversalExportModalComponent;
